import { type LucideIcon } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Skeleton } from '../ui/skeleton';

interface KpiCardProps {
  label: string;
  value: string;
  sub: string;
  icon: LucideIcon;
  color: 'green' | 'blue' | 'red' | 'orange' | 'purple';
  trend?: 'up' | 'down' | 'neutral';
  loading?: boolean;
}

const COLOR_MAP = {
  green:  { bg: 'bg-emerald-500/10', icon: 'text-emerald-500', value: 'text-emerald-600 dark:text-emerald-400', ring: 'ring-emerald-500/20' },
  blue:   { bg: 'bg-blue-500/10',    icon: 'text-blue-500',    value: 'text-blue-600 dark:text-blue-400',       ring: 'ring-blue-500/20' },
  red:    { bg: 'bg-red-500/10',     icon: 'text-red-500',     value: 'text-red-600 dark:text-red-400',         ring: 'ring-red-500/20' },
  orange: { bg: 'bg-orange-500/10',  icon: 'text-orange-500',  value: 'text-orange-600 dark:text-orange-400',   ring: 'ring-orange-500/20' },
  purple: { bg: 'bg-violet-500/10',  icon: 'text-violet-500',  value: 'text-violet-600 dark:text-violet-400',   ring: 'ring-violet-500/20' },
};

const TREND_MAP = {
  up:      { icon: '↑', cls: 'text-emerald-500' },
  down:    { icon: '↓', cls: 'text-red-500' },
  neutral: { icon: '→', cls: 'text-muted-foreground' },
};

export function KpiCard({ label, value, sub, icon: Icon, color, trend, loading }: KpiCardProps) {
  const c = COLOR_MAP[color];

  if (loading) {
    return (
      <div className="rounded-xl border border-border bg-card p-5 space-y-3">
        <Skeleton className="h-4 w-24" />
        <Skeleton className="h-8 w-32" />
        <Skeleton className="h-3 w-20" />
      </div>
    );
  }

  return (
    <div className={cn(
      'group relative rounded-xl border border-border bg-card p-5',
      'transition-all duration-200 hover:shadow-card-md hover:-translate-y-0.5',
    )}>
      {/* Icon badge */}
      <div className={cn('mb-3 inline-flex rounded-lg p-2 ring-1', c.bg, c.ring)}>
        <Icon className={cn('h-4 w-4', c.icon)} strokeWidth={2} />
      </div>

      {/* Label */}
      <p className="text-xs font-medium text-muted-foreground mb-1">{label}</p>

      {/* Value + trend */}
      <div className="flex items-end gap-2">
        <span className={cn('text-2xl font-bold tracking-tight', c.value)}>{value}</span>
        {trend && (
          <span className={cn('mb-0.5 text-sm font-semibold', TREND_MAP[trend].cls)}>
            {TREND_MAP[trend].icon}
          </span>
        )}
      </div>

      {/* Sub-label */}
      <p className="mt-1 text-xs text-muted-foreground">{sub}</p>

      {/* Subtle colored bottom bar */}
      <div className={cn(
        'absolute bottom-0 left-4 right-4 h-0.5 rounded-full opacity-0 transition-opacity group-hover:opacity-100',
        c.bg.replace('/10', '/50')
      )} />
    </div>
  );
}
